import React, { useState, useEffect, useContext } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";

import { Dropdown } from "react-bootstrap";
import LogoutPage from "./Logout";
import api from "../../../api";
import swal from "sweetalert";
import { getModuleInfo, getRelativeTime } from "../../components/Dashboard/NotificationHelper";
import { FaBell, FaCheckDouble, FaAngleRight, FaSun, FaMoon, FaLaptop, FaUserCircle } from "react-icons/fa";
import { ThemeContext } from "../../../context/ThemeContext";

const Header = ({ onNote }) => {
 const navigate = useNavigate();
 const dispatch = useDispatch();
 const location = useLocation();
 const { themeMode, changeThemeMode } = useContext(ThemeContext);
 const API_BASE = process.env.REACT_APP_API_URL;
 const getImageUrl = (path) => {
 if (!path) return null;
 if (path.startsWith("http")) return path;
 return `${API_BASE.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
 };

 // USER
 const [user, setUser] = useState(null);
 const [userReady, setUserReady] = useState(false);

 // NOTIFICATIONS
 const [notifications, setNotifications] = useState([]);
 const [unread, setUnread] = useState(0);
 const [openDropdown, setOpenDropdown] = useState(false);

 // SEARCH BAR
 const [searchText, setSearchText] = useState("");

 // Close dropdown whenever page changes
 useEffect(() => {
 setOpenDropdown(false);
 }, [location]);

 // ========================
 // Load User
 // ========================
 const loadUser = async () => {
 try {
 const res = await api.get("/auth/me");
 const userData = res.data?.data;
 setUser(userData);
 if (userData) {
 dispatch({ type: "UPDATE_USER_DETAILS", payload: userData });
 }
 } catch (err) {
 console.log("User Load Error:", err);
 } finally {
 setUserReady(true);
 }
 };

 // ========================
 // Load Notifications
 // ========================
 const loadNotifications = async () => {
 try {
 const role = (user?.role?.name || user?.roleName || (typeof user?.role === 'string' ? user?.role : "")).toLowerCase().trim();
 const isAdmin = role === "admin" || role === "superadmin" || role === "super admin";
 const endpoint = isAdmin ? "/notifications/all" : "/notifications";

 const res = await api.get(endpoint, {
 headers: {
 "Cache-Control": "no-cache",
 Pragma: "no-cache",
 Expires: "0",
 },
 });

 const list = res.data?.data || [];
 const unreadCount =
 res.data?.unreadCount !== undefined
 ? res.data.unreadCount
 : list.filter((n) => !n.isRead).length;
 
 setNotifications(list);
 setUnread(unreadCount);
 } catch (err) {
 console.log("Notification Error:", err);
 }
 };

 useEffect(() => {
 loadUser();
 }, []);

 useEffect(() => {
 if (!user) return;

 const interval = setInterval(loadNotifications, 10000);
 loadNotifications();
 return () => clearInterval(interval);
 }, [user]);

 // ========================
 // Mark All Notifications as Read
 // ========================
 const markAllAsRead = async () => {
 try {
 // CORRECT BACKEND ROUTE:
 await api.patch("/notifications/read-all");

 // Update UI immediately
 setUnread(0);
 setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
 } catch (err) {
 console.log("Mark All Read Error:", err);
 }
 };

 // ========================
 // Search Handler
 // ========================
 const handleSearch = (e) => {
 e.preventDefault();

 const allowed = /^[A-Za-z0-9 _-]+$/;

 if (!searchText.trim()) {
 swal("Oops!", "Please enter something to search!", "warning");
 return;
 }

 if (!allowed.test(searchText)) {
 swal("Invalid Search!", "Special characters are not allowed.", "error");
 return;
 }

 navigate(`/search?query=${encodeURIComponent(searchText)}`);
 };

 // Date formatter
 const formatDate = (d) => {
 const date = new Date(d);
 return `${date.toLocaleDateString()} - ${date.toLocaleTimeString([], {
 hour: "2-digit",
 minute: "2-digit",
 })}`;
 };

 const getInitials = (name) => (name ? name.charAt(0).toUpperCase() : "N");

 // Page Name
 const segments = location.pathname.split("/").filter(Boolean);
  let lastSegment = segments[segments.length - 1] || "";
  if (/^[a-f\d]{24}$/i.test(lastSegment) && segments.length > 1) {
    const prev = segments[segments.length - 2] || "";
    const parent = segments[segments.length - 3] || "";
    lastSegment = parent ? `${parent} ${prev}` : prev;
  }
  const page = lastSegment.replace(/-/g, " ");
 const pageName = page || "Dashboard";

 return (
 <div className="header border-bottom">
 <div className="header-content">
 <nav className="navbar navbar-expand">
 <div className="collapse navbar-collapse justify-content-between">
 {/* PAGE TITLE */}
 <div className="header-left">
 <div
 className="dashboard_bar"
 style={{ textTransform: "capitalize" }}
 >
 {pageName}
 </div>
 </div>

 <ul className="navbar-nav header-right">
 {/* SEARCH BAR */}
 {/* <li className="nav-item d-flex align-items-center">
 <form className="input-group search-area" onSubmit={handleSearch}>
 <input
 type="text"
 value={searchText}
 onChange={(e) => setSearchText(e.target.value)}
 className="form-control"
 placeholder="Search here..."
 />
 <button className="input-group-text" type="submit">
 <i className="flaticon-381-search-2"></i>
 </button>
 </form>
 </li> */}

 {/* NOTIFICATION DROPDOWN */}
 <Dropdown
 as="li"
 className="nav-item dropdown notification_dropdown"
 show={openDropdown}
 onToggle={(isOpen) => setOpenDropdown(isOpen)}
 >
 <Dropdown.Toggle
 className="nav-link c-pointer i-false position-relative"
 variant=""
 as="a"
 style={{ display: "flex", alignItems: "center", padding: "10px" }}
 >
 <div className="notification-bell-icon" style={{ position: 'relative', display: 'inline-block', width: '24px', height: '24px', lineHeight: '1' }}>
 <FaBell size={24} color="#0078D7" style={{ verticalAlign: 'top' }} />
 {unread > 0 && (
 <span className="notification-badge-dot">
 {unread > 9 ? '9+' : unread}
 </span>
 )}
 </div>
 </Dropdown.Toggle>

 <Dropdown.Menu
 align="right"
 className="mt-2 dropdown-menu dropdown-menu-end shadow-lg border-0 rounded-4 overflow-hidden"
 style={{ width: "380px", background: "#ffffff", padding: "0" }}
 >
 <div className="dropdown-header-custom d-flex justify-content-between align-items-center p-3 border-bottom">
 <h5 className="mb-0 fw-bold text-dark fs-16">Notifications</h5>
 {unread > 0 && (
 <button 
 className="btn btn-link btn-sm text-primary p-0 text-decoration-none fs-12 d-flex align-items-center gap-1"
 onClick={(e) => {
 e.stopPropagation();
 markAllAsRead();
 }}
 >
 <FaCheckDouble size={12} /> Mark all read
 </button>
 )}
 </div>

 <PerfectScrollbar className="widget-media dlab-scroll height380">
 <ul className="timeline p-3 m-0">
 {notifications.length === 0 && (
 <div className="text-center py-5">
 <div className="rounded-circle d-inline-flex p-3 mb-3" style={{ background: 'rgba(0,120,215,0.08)' }}>
 <FaBell size={30} color="#0078D7" style={{ opacity: 0.3 }} />
 </div>
 <p className="text-muted fs-14">No notifications yet</p>
 </div>
 )}

 {notifications.map((n) => {
 const { icon, color, bg } = getModuleInfo(n.module);
 return (
 <li key={n._id} className="mb-3">
 <div 
 className={`timeline-panel p-2 rounded-3 transition-all cursor-pointer d-flex align-items-start ${!n.isRead ? "bg-light-primary border-start border-primary border-3" : "bg-transparent border-0"}`}
 style={{ 
 background: !n.isRead ? "rgba(136, 108, 255, 0.04)" : "transparent",
 transition: "all 0.2s ease"
 }}
 >
 <div className="media me-3" 
 style={{ 
 width: "42px", 
 height: "42px", 
 minWidth: "42px",
 display: "flex", 
 alignItems: "center", 
 justifyContent: "center", 
 borderRadius: "12px",
 background: bg,
 color: color,
 fontSize: "18px"
 }}>
 {icon}
 </div>

 <div className="media-body overflow-hidden">
 <div className="d-flex justify-content-between align-items-center mb-1">
 <span className="text-uppercase fw-bold fs-10" style={{ color: color, letterSpacing: '0.5px' }}>{n.module}</span>
 <span className="text-muted fs-10">{getRelativeTime(n.createdAt)}</span>
 </div>
 <p className="mb-0 text-dark fs-13 text-truncate-2 fw-medium">
 {n.message}
 </p>
 </div>
 </div>
 </li>
 );
 })}
 </ul>
 </PerfectScrollbar>

 <div className="p-3 notif-footer text-center">
 <Link 
 to="/notifications" 
 className="fw-bold fs-13 d-flex align-items-center justify-content-center gap-2"
 style={{ color: '#0078D7', textDecoration: 'none' }}
 onClick={() => setOpenDropdown(false)}
 >
 View all notifications <FaAngleRight size={12} />
 </Link>
 </div>
 </Dropdown.Menu>
 </Dropdown>

 <style>{`
 .notification-badge-dot {
 position: absolute;
 top: -9px;
 right: -11px;
 min-width: 20px;
 height: 20px;
 background: linear-gradient(135deg, #FF4757, #FF6B81);
 border: 2.5px solid var(--headerbg, #fff);
 border-radius: 12px;
 color: #fff;
 font-size: 9px;
 font-weight: 700;
 display: flex;
 align-items: center;
 justify-content: center;
 padding: 0 5px;
 line-height: 1;
 letter-spacing: 0.3px;
 box-shadow: 0 2px 8px rgba(255,71,87,0.45);
 animation: badge-pulse 2s infinite;
 }
 @keyframes badge-pulse {
 0%, 100% { box-shadow: 0 2px 8px rgba(255,71,87,0.45); }
 50% { box-shadow: 0 2px 14px rgba(255,71,87,0.75); }
 }
 .theme-active-item {
 background: linear-gradient(135deg, rgba(0,120,215,0.1), rgba(136,108,255,0.08)) !important;
 border-left: 3px solid #0078D7;
 color: #0078D7 !important;
 }
 .theme-inactive-item {
 border-left: 3px solid transparent;
 }
 .theme-inactive-item:hover {
 background: rgba(0,120,215,0.05) !important;
 }
 .notif-footer {
 background: linear-gradient(135deg, rgba(0,120,215,0.04), rgba(136,108,255,0.04));
 border-top: 1px solid rgba(0,120,215,0.1) !important;
 }
 .line-clamp-2 {
 display: -webkit-box;
 -webkit-line-clamp: 2;
 -webkit-box-orient: vertical;
 overflow: hidden;
 }
 .transition-all {
 transition: all 0.2s ease;
 }
 .timeline-panel:hover {
 transform: translateX(5px);
 background: rgba(0, 120, 215, 0.06) !important;
 }
 .fs-16 { font-size: 16px; }
 .fs-10 { font-size: 10px; }
 `}</style>

 {/* THEME SELECTOR */}
 <Dropdown as="li" className="nav-item dropdown theme-mode-dropdown me-2">
 <Dropdown.Toggle
 className="nav-link c-pointer i-false position-relative"
 variant=""
 as="a"
 style={{ display: "flex", alignItems: "center", padding: "10px" }}
 >
 <div className="theme-toggle-icon" style={{ display: 'flex', alignItems: 'center' }}>
 {themeMode === "light" && <FaSun size={20} color="#F47920" />}
 {themeMode === "dark" && <FaMoon size={20} color="#886cc0" />}
 {themeMode === "system" && <FaLaptop size={20} color="#0078D7" />}
 </div>
 </Dropdown.Toggle>

 <Dropdown.Menu
 align="end"
 className="mt-2 dropdown-menu dropdown-menu-end shadow-lg border-0 rounded-4 overflow-hidden"
 style={{ minWidth: "160px", background: "#ffffff", padding: "0" }}
 >
 <Dropdown.Item 
 onClick={() => changeThemeMode("light")}
 className={`d-flex align-items-center gap-3 p-3 fs-14 fw-medium ${themeMode === "light" ? "theme-active-item" : "text-dark theme-inactive-item"}`}
 >
 <FaSun size={16} className="text-warning" /> Light Mode
 </Dropdown.Item>
 <Dropdown.Item 
 onClick={() => changeThemeMode("dark")}
 className={`d-flex align-items-center gap-3 p-3 fs-14 fw-medium ${themeMode === "dark" ? "theme-active-item" : "text-dark theme-inactive-item"}`}
 >
 <FaMoon size={16} className="text-info" /> Dark Mode
 </Dropdown.Item>
 <Dropdown.Item 
 onClick={() => changeThemeMode("system")}
 className={`d-flex align-items-center gap-3 p-3 fs-14 fw-medium ${themeMode === "system" ? "theme-active-item" : "text-dark theme-inactive-item"}`}
 >
 <FaLaptop size={16} className="text-secondary" /> Auto (System)
 </Dropdown.Item>
 </Dropdown.Menu>
 </Dropdown>

 {/* USER MENU */}
 <Dropdown as="li" className="nav-item dropdown header-profile ">
 <Dropdown.Toggle
 variant=""
 as="a"
 className="nav-link i-false"
 style={{ display: "flex", alignItems: "center" }}
 >
 {userReady ? (
 <>
 {getImageUrl(user?.image) ? (
 <img
 src={getImageUrl(user?.image)}
 width={35}
 height={35}
 alt="profile"
 style={{ borderRadius: "50%", objectFit: "cover" }}
 onError={(e) => {
 e.target.style.display = "none";
 e.target.nextSibling.style.display = "flex";
 }}
 />
 ) : null}
 <span
 style={{
 display: getImageUrl(user?.image) ? "none" : "flex",
 width: 35,
 height: 35,
 borderRadius: "50%",
 background: "linear-gradient(135deg, #0078D7, #886cc0)",
 alignItems: "center",
 justifyContent: "center",
 color: "#fff",
 fontSize: "18px",
 flexShrink: 0,
 }}
 >
 <FaUserCircle size={22} />
 </span>

 <div className="header-info ms-3">
 <span className="fs-18 font-w500">
 {user?.name || "User"}
 </span>
 <small className="fs-12 text-muted">
 {user?.email || ""}
 </small>
 </div>
 </>
 ) : (
 <div style={{ width: 35, height: 35 }} aria-hidden="true" />
 )}
 </Dropdown.Toggle>

 <Dropdown.Menu className="mt-3 dropdown-menu-end shadow border-0 rounded-3" style={{ minWidth: '180px' }}>
 <Link to="/user-profile" className="dropdown-item d-flex align-items-center gap-2">
 <i className="fas fa-user-circle text-primary"></i> Profile
 </Link>
 <LogoutPage />
 </Dropdown.Menu>
 </Dropdown>
 </ul>
 </div>
 </nav>
 </div>
 </div>
 );
};

export default Header;
